import { useEffect, useMemo, useRef, useState } from 'react';

import { SelectionToolbar } from '@/components/ielts/SelectionToolbar';
import { QuestionNav } from '@/components/ielts/QuestionNav';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ieltsReadingApi,
  TF_NG_OPTIONS,
  YN_NG_OPTIONS,
  type ReadingAnswers,
  type ReadingAnswerValue,
  type ReadingQuestion,
  type ReadingReviewItem,
  type ReadingSubmission,
  type ReadingTest,
} from '@/api/ielts';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { clearDraft, loadDraft, saveDraft } from '@/lib/answerDraft';
import { useOpenTest } from '@/hooks/useOpenTest';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { SourceSplit } from '@/components/ielts/SourceSplit';

const TAB_IDS = ['practice', 'history'] as const;
type TabId = (typeof TAB_IDS)[number];

/** Band → 0..1 fill for the overall ring (IELTS max is 9). */
function bandFraction(band?: number): number {
  if (typeof band !== 'number') return 0;
  return Math.max(0, Math.min(1, band / 9));
}

export function StudentIELTSReadingPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>('practice');

  const TABS: { id: TabId; label: string }[] = [
    { id: 'practice', label: t('ielts.reading.tabPractice') },
    { id: 'history', label: t('ielts.reading.tabHistory') },
  ];

  return (
    <>
      <PageHeader title={t('ielts.reading.title')} subtitle={t('ielts.reading.subtitle')} />
      <div className="mb-6 flex gap-2">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              'rounded-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-label transition',
              tab === tb.id
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container-lowest text-on-surface-variant ghost-border hover:bg-surface-container-low',
            )}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'practice' && <PracticeTab />}
      {tab === 'history' && <HistoryTab />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Practice tab: test picker → take view → result card
// ---------------------------------------------------------------------------

function PracticeTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [result, setResult] = useState<ReadingSubmission | null>(null);

  const tests = useQuery({
    queryKey: ['ielts-reading', 'tests'],
    queryFn: () => ieltsReadingApi.listTests(),
  });

  // Открытый тест — шаг истории (`?test=<id>`, hooks/useOpenTest.ts): «назад» возвращает на этот список,
  // а не уводит со страницы. Адрес с параметром после перезапуска или с чужим id — список.
  const openTest = useOpenTest(tests.data?.map((x) => x.id));
  const selectedId = openTest.id;
  // Вернулись на список системным «назад» с карточки результата — результат больше не показываем.
  useEffect(() => {
    if (!selectedId) setResult(null);
  }, [selectedId]);

  const onGraded = (sub: ReadingSubmission) => {
    setResult(sub);
    qc.invalidateQueries({ queryKey: ['ielts-reading', 'history'] });
    qc.invalidateQueries({ queryKey: ['ielts-reading', 'progress'] });
  };

  function backToPicker() {
    setResult(null);
    openTest.close();
  }

  // ----- Result card -----
  if (result && selectedId) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={backToPicker}>
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('ielts.reading.backToTests')}
        </Button>
        <ResultCard submission={result} />
      </div>
    );
  }

  // ----- Take view -----
  if (selectedId) {
    return (
      <TakeView
        testId={selectedId}
        onExit={backToPicker}
        onGraded={onGraded}
      />
    );
  }

  // ----- Test picker -----
  if (tests.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (tests.isError) {
    return (
      <Card variant="subtle" className="text-error">
        {humanizeError(tests.error)}
      </Card>
    );
  }
  if (!tests.data?.length) {
    return (
      <Card variant="subtle" className="text-center text-on-surface-variant">
        <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/60">
          menu_book
        </span>
        {t('ielts.reading.noTests')}
      </Card>
    );
  }

  return (
    <ul className="grid gap-3 md:grid-cols-2">
      {tests.data.map((test) => (
        <li key={test.id}>
          <TestCard test={test} onStart={() => openTest.open(test.id)} />
        </li>
      ))}
    </ul>
  );
}

function TestCard({ test, onStart }: { test: ReadingTest; onStart: () => void }) {
  const { t } = useTranslation();
  const count = test.questions?.length ?? 0;
  return (
    <Card interactive onClick={onStart} className="h-full cursor-pointer">
      <div className="mb-2 flex items-center gap-2">
        <Pill variant="primary">{t('ielts.reading.questionCount', { count })}</Pill>
        {typeof test.duration_min === 'number' && (
          <Pill variant="neutral">{t('ielts.reading.durationMin', { count: test.duration_min })}</Pill>
        )}
      </div>
      <CardTitle>{test.title}</CardTitle>
      <div className="mt-4">
        <Button size="sm" variant="secondary" onClick={onStart}>
          {t('ielts.reading.startTest')}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Take view: passage pane + questions pane + optional countdown timer
// ---------------------------------------------------------------------------

function TakeView({
  testId,
  onExit,
  onGraded,
}: {
  testId: string;
  onExit: () => void;
  onGraded: (sub: ReadingSubmission) => void;
}) {
  const { t } = useTranslation();

  const test = useQuery({
    queryKey: ['ielts-reading', 'test', testId],
    queryFn: () => ieltsReadingApi.getTest(testId),
  });

  /**
   * ⚠️ ОТВЕТЫ ПОДНИМАЕМ ИЗ ЧЕРНОВИКА, А НЕ НАЧИНАЕМ С ПУСТОГО. Раньше они жили
   * только в памяти вкладки: любой уход со страницы — случайное «назад»,
   * звонок на телефоне, перезагрузка — стирал всю работу. Жалоба заказчика
   * от 07.09.2026 была ровно об этом.
   */
  const draftKey = `reading:${testId}`;
  const [answers, setAnswers] = useState<ReadingAnswers>(
    () => loadDraft<ReadingAnswers>(draftKey) ?? {},
  );

  // Пишем на каждое изменение: тут нечего беречь, запись идёт на устройство.
  useEffect(() => {
    saveDraft(draftKey, answers);
  }, [draftKey, answers]);

  // Computer-based-test shell: fullscreen "Focus" mode + a draggable divider
  // between the passage and the questions (like a real IELTS reading screen).
  const passageRef = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState(false);
  const [wide, setWide] = useState(false);
  // On phones the two-pane split is unusable, so we show ONE pane at a time
  // with a segmented toggle (passage ⇄ questions), like a real mobile CBT.
  const [mobileView, setMobileView] = useState<'passage' | 'questions'>('passage');
  const [activeIndex, setActiveIndex] = useState(0);
  const [activePart, setActivePart] = useState(0);
  const qRefs = useRef<Record<string, HTMLLIElement | null>>({});
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const submit = useMutation({
    mutationFn: (body: { test_id: string; answers: ReadingAnswers }) =>
      ieltsReadingApi.submit(body),
    onSuccess: (r) => {
      // Иначе при следующем заходе старые ответы лягут на чистый бланк
      // и человек решит, что сдача не прошла.
      clearDraft(draftKey);
      onGraded(r);
    },
  });

  const setAnswer = (qid: string, value: ReadingAnswerValue) =>
    setAnswers((prev) => ({ ...prev, [qid]: value }));

  const doSubmit = () => submit.mutate({ test_id: testId, answers });

  if (test.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (test.isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onExit}>
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('ielts.reading.backToTests')}
        </Button>
        <Card variant="subtle" className="text-error">{humanizeError(test.error)}</Card>
      </div>
    );
  }
  const data = test.data!;
  const questions = data.questions ?? [];
  const answeredFlags = questions.map(
    (q) => answers[q.id] !== undefined && answers[q.id] !== '',
  );
  const answered = answeredFlags.filter(Boolean).length;

  // Multi-part test (Reading Passage 1/2/3): show ONE part at a time — its
  // passage in the left pane and only its questions on the right. Legacy
  // single-passage tests (no parts) fall through to a single implicit part.
  const parts = data.parts && data.parts.length > 0 ? data.parts : null;
  const partOf = (i: number) => questions[i]?.part_index ?? 0;
  const activePassage = parts ? (parts[activePart]?.passage_html ?? '') : data.passage_html;

  // Bottom-nav groups: one per part, spanning its (contiguous) question numbers.
  const navGroups = parts
    ? parts
        .map((p, pi) => {
          const idxs = questions
            .map((_, i) => i)
            .filter((i) => partOf(i) === pi);
          return idxs.length
            ? { label: p.label || `Part ${pi + 1}`, from: idxs[0] + 1, to: idxs[idxs.length - 1] + 1 }
            : null;
        })
        .filter((g): g is { label: string; from: number; to: number } => g !== null)
    : undefined;

  // Jump to a question from the bottom nav: switch to its part, on mobile switch
  // to the questions pane, then scroll the target into view and mark it active.
  const jumpTo = (index: number) => {
    const q = questions[index];
    if (!q) return;
    setActiveIndex(index);
    if (parts) setActivePart(partOf(index));
    if (!wide) setMobileView('questions');
    requestAnimationFrame(() =>
      qRefs.current[q.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    );
  };

  const paneCls = focus ? 'h-[calc(100vh-7rem)] overflow-y-auto' : 'overflow-y-auto lg:max-h-[82vh]';

  return (
    <div className={focus ? 'fixed inset-0 z-50 flex flex-col gap-3 overflow-auto bg-background p-4' : 'space-y-4'}>
      {/* Test toolbar — exit · timer · Focus fullscreen */}
      <div className="flex items-center justify-between gap-3 rounded-card bg-surface-container-lowest px-3 py-2 shadow-soft">
        <Button variant="ghost" size="sm" onClick={onExit} disabled={submit.isPending}>
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('ielts.reading.exit')}
        </Button>
        {typeof data.duration_min === 'number' && data.duration_min > 0 && (
          <CountdownTimer minutes={data.duration_min} onExpire={doSubmit} />
        )}
        <Button variant="ghost" size="sm" onClick={() => setFocus((f) => !f)}>
          <span className="material-symbols-outlined text-base">{focus ? 'close_fullscreen' : 'open_in_full'}</span>
          {t('ielts.reading.focus')}
        </Button>
      </div>

      {/* Part switcher (Reading Passage 1/2/3) — only for multi-part tests */}
      {parts && parts.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {parts.map((p, pi) => (
            <button
              key={pi}
              type="button"
              onClick={() => {
                setActivePart(pi);
                if (!wide) setMobileView('passage');
              }}
              className={cn(
                'shrink-0 rounded-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-label transition',
                activePart === pi
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-container-lowest text-on-surface-variant ghost-border hover:bg-surface-container-low',
              )}
            >
              {p.label || t('ielts.reading.partN', { n: pi + 1 })}
            </button>
          ))}
        </div>
      )}

      {/* Текст и вопросы: две колонки на компьютере, вкладки на телефоне.
          Раскладка вынесена в общий компонент — она нужна ещё моку, спринту
          и тренажёру, а копии неизбежно разъезжаются. */}
      <SourceSplit
        className="flex min-h-0 flex-1 flex-col"
        // Вкладкой управляет страница: нажатие на номер вопроса внизу должно
        // САМО перевести на панель вопросов, иначе ученик тычет в вопрос и
        // продолжает смотреть в текст.
        pane={mobileView === 'passage' ? 'source' : 'questions'}
        onPaneChange={(v) => setMobileView(v === 'source' ? 'passage' : 'questions')}
        source={
          <div className="relative">
            <SelectionToolbar containerRef={passageRef} />
            <Card className={paneCls}>
              <CardTitle>{parts ? (parts[activePart]?.label || data.title) : data.title}</CardTitle>
              <div
                ref={passageRef}
                className="ielts-reading-passage prose-reading mt-3 max-w-none text-sm leading-relaxed text-on-surface [&_h1]:mt-4 [&_h1]:text-base [&_h1]:font-bold [&_h2]:mt-4 [&_h2]:text-sm [&_h2]:font-bold [&_p]:mt-3"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(activePassage) }}
              />
            </Card>
          </div>
        }
        questions={
            <Card className={paneCls}>
            <div className="flex items-baseline justify-between">
              <CardTitle>{t('ielts.reading.questions')}</CardTitle>
              <span className="text-xs font-semibold text-on-surface-variant">
                {t('ielts.reading.answeredOf', { answered, total: questions.length })}
              </span>
            </div>
  
            <ol className="mt-4 space-y-5">
              {questions
                .map((q, i) => ({ q, i }))
                .filter(({ i }) => !parts || partOf(i) === activePart)
                .map(({ q, i }) => (
                  <li
                    key={q.id}
                    ref={(el) => {
                      qRefs.current[q.id] = el;
                    }}
                    className={cn(
                      'scroll-mt-4 rounded-card transition',
                      activeIndex === i && '-mx-2 bg-primary-container/30 px-2 py-1 ring-1 ring-primary/30',
                    )}
                  >
                    <QuestionInput
                      index={i + 1}
                      question={q}
                      value={answers[q.id]}
                      onChange={(v) => {
                        setAnswer(q.id, v);
                        setActiveIndex(i);
                      }}
                    />
                  </li>
                ))}
            </ol>
  
            {submit.isError && (
              <p className="mt-4 text-sm text-error">{humanizeError(submit.error)}</p>
            )}
  
            <div className="mt-6 flex justify-end">
              <Button
                onClick={doSubmit}
                loading={submit.isPending}
                disabled={submit.isPending || answered === 0}
              >
                <span className="material-symbols-outlined">grading</span>
                {t('ielts.reading.submit')}
              </Button>
            </div>
            </Card>
        }
      />

      {/* Bottom part-switcher with numbered chips (engnovate-style, our colours) */}
      <QuestionNav
        total={questions.length}
        answered={answeredFlags}
        activeIndex={activeIndex}
        onJump={jumpTo}
        groups={navGroups}
      />
    </div>
  );
}

function CountdownTimer({ minutes, onExpire }: { minutes: number; onExpire: () => void }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(minutes * 60);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (expired) return;
    if (remaining <= 0) {
      setExpired(true);
      onExpire();
      return;
    }
    const id = window.setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => window.clearTimeout(id);
    // onExpire is stable enough for this single-shot timer; deliberately omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, expired]);

  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, '0');
  const low = remaining <= 60;

  return (
    <Pill variant={low ? 'danger' : 'warning'}>
      <span className="material-symbols-outlined text-sm">timer</span>
      {t('ielts.reading.timeLeft', { time: `${mm}:${ss}` })}
    </Pill>
  );
}

// ---------------------------------------------------------------------------
// Per-type question input
// ---------------------------------------------------------------------------

function QuestionInput({
  index,
  question,
  value,
  onChange,
}: {
  index: number;
  question: ReadingQuestion;
  value: ReadingAnswerValue | undefined;
  onChange: (v: ReadingAnswerValue) => void;
}) {
  const { t } = useTranslation();
  const name = `q-${question.id}`;
  const isIndexRadio =
    question.type === 'multiple_choice' ||
    question.type === 'matching_headings' ||
    question.type === 'phrase_bank' ||
    question.type === 'map_labeling';

  return (
    <div>
      {/* Optional group heading (e.g. "Questions 14–19" + instruction) */}
      {question.group_label && (
        <div className="mb-3 border-l-2 border-primary/40 pl-3">
          <p className="text-sm font-bold text-on-surface">{question.group_label}</p>
          {question.group_instruction && (
            <p className="mt-0.5 text-xs text-on-surface-variant">{question.group_instruction}</p>
          )}
        </div>
      )}

      <p className="text-sm font-semibold text-on-surface">
        <span className="text-primary">{index}.</span> {question.prompt}
      </p>

      {/* Optional image (map / plan / diagram for map_labeling) */}
      {question.image_url && (
        <img
          src={question.image_url}
          alt={t('ielts.reading.questionImage')}
          className="mt-3 w-full rounded-card object-contain ring-1 ring-inset ring-outline-variant/20"
        />
      )}

      {/* multiple_choice / matching_headings / phrase_bank / map_labeling
          → single choice over options (index answer) */}
      {isIndexRadio && (
        <div className="mt-2 space-y-1.5">
          {(question.options ?? []).map((opt, oi) => (
            <RadioRow
              key={oi}
              name={name}
              label={opt}
              checked={value === oi}
              onSelect={() => onChange(oi)}
            />
          ))}
        </div>
      )}

      {/* multi_select → checkboxes; value is a sorted comma-joined index set */}
      {question.type === 'multi_select' && (
        <MultiSelect
          options={question.options ?? []}
          pickCount={question.pick_count}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
        />
      )}

      {/* true_false_ng → radio over the fixed enum */}
      {question.type === 'true_false_ng' && (
        <div className="mt-2 space-y-1.5">
          {TF_NG_OPTIONS.map((opt) => (
            <RadioRow
              key={opt}
              name={name}
              label={t(`ielts.reading.tfng.${opt}`)}
              checked={value === opt}
              onSelect={() => onChange(opt)}
            />
          ))}
        </div>
      )}

      {/* yes_no_ng → radio over the fixed enum */}
      {question.type === 'yes_no_ng' && (
        <div className="mt-2 space-y-1.5">
          {YN_NG_OPTIONS.map((opt) => (
            <RadioRow
              key={opt}
              name={name}
              label={t(`ielts.reading.ynng.${opt}`)}
              checked={value === opt}
              onSelect={() => onChange(opt)}
            />
          ))}
        </div>
      )}

      {/* sentence_completion / short_answer → free text */}
      {(question.type === 'sentence_completion' || question.type === 'short_answer') && (
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('ielts.reading.answerPlaceholder')}
          className="mt-2 w-full rounded-button bg-surface-container-low px-4 py-2 text-sm text-on-surface outline-none ring-1 ring-inset ring-outline-variant/30 focus:ring-2 focus:ring-primary"
        />
      )}
    </div>
  );
}

/**
 * "Choose TWO/THREE letters" — checkboxes whose value is a sorted, comma-joined
 * set of option indices (matches the backend multi_select answer key). When a
 * pickCount is set, selecting beyond it drops the oldest pick so the student can
 * never over-select.
 */
function MultiSelect({
  options,
  pickCount,
  value,
  onChange,
}: {
  options: string[];
  pickCount?: number;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const chosen = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '')
    .map(Number)
    .filter((n) => Number.isInteger(n));

  const toggle = (oi: number) => {
    let next: number[];
    if (chosen.includes(oi)) {
      next = chosen.filter((n) => n !== oi);
    } else if (pickCount && chosen.length >= pickCount) {
      // Drop the oldest pick to stay within the limit.
      next = [...chosen.slice(1), oi];
    } else {
      next = [...chosen, oi];
    }
    next.sort((a, b) => a - b);
    onChange(next.join(','));
  };

  return (
    <div className="mt-2 space-y-1.5">
      {pickCount ? (
        <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
          {t('ielts.reading.chooseN', { count: pickCount })}
        </p>
      ) : null}
      {options.map((opt, oi) => {
        const checked = chosen.includes(oi);
        return (
          <label
            key={oi}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-button px-3 py-2 text-sm ring-1 ring-inset transition',
              checked
                ? 'bg-primary-container text-on-primary-container ring-primary'
                : 'bg-surface-container-low text-on-surface ring-outline-variant/20 hover:bg-surface-container',
            )}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(oi)}
              className="accent-primary"
            />
            <span>{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

function RadioRow({
  name,
  label,
  checked,
  onSelect,
}: {
  name: string;
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-button px-3 py-2 text-sm ring-1 ring-inset transition',
        checked
          ? 'bg-primary-container text-on-primary-container ring-primary'
          : 'bg-surface-container-low text-on-surface ring-outline-variant/20 hover:bg-surface-container',
      )}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        className="accent-primary"
      />
      <span>{label}</span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Result card: overall band + raw/total + per-question review
// ---------------------------------------------------------------------------

function ResultCard({ submission }: { submission: ReadingSubmission }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <OverallBand band={submission.band} />
          <div className="flex-1">
            <CardTitle>{t('ielts.reading.yourBand')}</CardTitle>
            <p className="mt-2 text-sm text-on-surface-variant">
              {t('ielts.reading.scoreSummary', {
                raw: submission.raw_score,
                total: submission.total,
              })}
            </p>
          </div>
        </div>
      </Card>

      <ReviewList review={submission.review ?? []} />
    </div>
  );
}

function ReviewList({ review }: { review: ReadingReviewItem[] }) {
  const { t } = useTranslation();
  if (!review.length) return null;
  return (
    <Card>
      <CardTitle>{t('ielts.reading.reviewTitle')}</CardTitle>
      <ul className="mt-3 space-y-2">
        {review.map((item, i) => (
          <li
            key={item.question_id ?? i}
            className="flex items-start gap-3 rounded-card bg-surface-container-low p-3 ring-1 ring-inset ring-outline-variant/20"
          >
            <span
              className={cn(
                'material-symbols-outlined mt-0.5 text-lg',
                item.correct ? 'text-success' : 'text-error',
              )}
            >
              {item.correct ? 'check_circle' : 'cancel'}
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-on-surface">
                {t('ielts.reading.questionN', { n: i + 1 })}
              </p>
              <p className="mt-0.5 text-xs text-on-surface-variant">
                {t('ielts.reading.correctAnswer')}:{' '}
                <span className="font-semibold text-on-surface">
                  {formatAnswer(item.correct_answer)}
                </span>
              </p>
            </div>
            <Pill variant={item.correct ? 'success' : 'danger'}>
              {item.correct ? t('ielts.reading.correct') : t('ielts.reading.incorrect')}
            </Pill>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Render a correct-answer value (index or string/enum) for display. */
function formatAnswer(value: ReadingAnswerValue): string {
  return String(value);
}

function OverallBand({ band }: { band?: number }) {
  const { t } = useTranslation();
  const frac = bandFraction(band);
  const deg = Math.round(frac * 360);
  return (
    <div className="flex flex-col items-center">
      <div
        className="grid h-32 w-32 place-items-center rounded-full"
        style={{
          background: `conic-gradient(#FF941F ${deg}deg, rgba(127,127,127,0.18) ${deg}deg)`,
        }}
      >
        <div className="grid h-24 w-24 place-items-center rounded-full bg-surface-container-lowest">
          <span className="font-display text-4xl font-extrabold text-on-surface">
            {typeof band === 'number' ? band.toFixed(1) : '—'}
          </span>
        </div>
      </div>
      <span className="mt-2 text-xs font-semibold uppercase tracking-label text-on-surface-variant">
        {t('ielts.reading.overall')}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab: list + band trend chart
// ---------------------------------------------------------------------------

function HistoryTab() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const history = useQuery({
    queryKey: ['ielts-reading', 'history'],
    queryFn: () => ieltsReadingApi.listSubmissions(),
  });
  const progress = useQuery({
    queryKey: ['ielts-reading', 'progress'],
    queryFn: () => ieltsReadingApi.getProgress(),
  });

  if (history.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (history.isError) {
    return (
      <Card variant="subtle" className="text-error">
        {humanizeError(history.error)}
      </Card>
    );
  }
  if (!history.data?.length) {
    return (
      <Card variant="subtle" className="text-center text-on-surface-variant">
        <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/60">
          history_edu
        </span>
        {t('ielts.reading.noHistory')}
      </Card>
    );
  }

  const prog = progress.data;
  return (
    <div className="space-y-4">
      {prog && prog.count > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card variant="subtle">
            <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('ielts.reading.attempts')}
            </p>
            <p className="mt-1 font-display text-3xl font-extrabold text-on-surface">
              {prog.count}
            </p>
          </Card>
          <Card variant="subtle">
            <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('ielts.reading.avgBand')}
            </p>
            <p className="mt-1 font-display text-3xl font-extrabold text-on-surface">
              {(prog.avg_band ?? 0).toFixed(1)}
            </p>
          </Card>
          <Card variant="subtle">
            <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('ielts.reading.latestBand')}
            </p>
            <p className="mt-1 font-display text-3xl font-extrabold text-on-surface">
              {(prog.latest_band ?? 0).toFixed(1)}
            </p>
          </Card>
        </div>
      )}

      {prog && prog.band_trend.length > 1 && <BandTrendChart points={prog.band_trend} />}

      <ul className="space-y-2">
        {history.data.map((sub) => (
          <li key={sub.id}>
            <HistoryRow
              submission={sub}
              expanded={openId === sub.id}
              onToggle={() => setOpenId(openId === sub.id ? null : sub.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function BandTrendChart({ points }: { points: { date: string; band: number }[] }) {
  const { t } = useTranslation();
  const { W, H, PAD, path, dots } = useMemo(() => {
    const w = 520;
    const h = 160;
    const pad = 24;
    const n = points.length;
    const max = 9;
    const x = (i: number) => pad + (i * (w - pad * 2)) / Math.max(1, n - 1);
    const y = (b: number) => h - pad - (b / max) * (h - pad * 2);
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.band)}`).join(' ');
    const ds = points.map((p, i) => ({ cx: x(i), cy: y(p.band), band: p.band }));
    return { W: w, H: h, PAD: pad, path: d, dots: ds };
  }, [points]);

  return (
    <Card>
      <CardTitle>{t('ielts.reading.bandTrend')}</CardTitle>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-3 h-40 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={t('ielts.reading.bandTrend')}
      >
        <line
          x1={PAD}
          y1={H - PAD}
          x2={W - PAD}
          y2={H - PAD}
          stroke="currentColor"
          strokeOpacity={0.15}
        />
        <path d={path} fill="none" stroke="#FF941F" strokeWidth={2.5} />
        {dots.map((d, i) => (
          <g key={i}>
            <circle cx={d.cx} cy={d.cy} r={4} fill="#FF941F" />
            <text
              x={d.cx}
              y={d.cy - 8}
              textAnchor="middle"
              className="fill-current text-[10px]"
            >
              {d.band.toFixed(1)}
            </text>
          </g>
        ))}
      </svg>
    </Card>
  );
}

function HistoryRow({
  submission,
  expanded,
  onToggle,
}: {
  submission: ReadingSubmission;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const detail = useQuery({
    queryKey: ['ielts-reading', 'submission', submission.id],
    queryFn: () => ieltsReadingApi.getSubmission(submission.id),
    enabled: expanded,
  });

  const date = submission.created_at
    ? new Date(submission.created_at).toLocaleDateString()
    : '';

  return (
    <Card variant="subtle" className="overflow-hidden p-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 p-4 text-left transition hover:bg-surface-container-low"
      >
        <span className="material-symbols-outlined text-primary">menu_book</span>
        <div className="flex-1">
          <p className="font-semibold text-on-surface">
            {submission.test_title ?? t('ielts.reading.readingTest')}
          </p>
          <p className="text-xs text-on-surface-variant">
            {date}
            {' · '}
            {t('ielts.reading.scoreShort', {
              raw: submission.raw_score,
              total: submission.total,
            })}
          </p>
        </div>
        {typeof submission.band === 'number' && (
          <Pill variant="primary">{submission.band.toFixed(1)}</Pill>
        )}
        <span className="material-symbols-outlined text-on-surface-variant">
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-outline-variant/20 p-4">
          {detail.isLoading && (
            <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>
          )}
          {detail.isError && (
            <p className="text-sm text-error">{humanizeError(detail.error)}</p>
          )}
          {detail.data && <ResultCard submission={detail.data} />}
        </div>
      )}
    </Card>
  );
}
